import { HonestState } from "../components.js";
import type { Metric, ThroughputForecastValue } from "../api/scenarioService.js";

/**
 * NX-2633 (E8.8) — the live-delta forecast ribbon (AC1, design/08 §2 flow 2's "watch the forecast
 * ribbon narrow as fixes are toggled"). No reusable `.ribbon` COMPONENT exists anywhere in this
 * repo — only the vendored `.ribbon .date`/`.chart .mk`/`.gain` CSS classes in project.css (ported
 * byte-for-byte from the locked demo, per this story's ground-truth note) — so this is the first
 * component to actually render them.
 *
 * The delta is a REAL COMPUTED DIFFERENCE (`scenario.p50 - baseline.p50`), never merely two numbers
 * placed side by side — WhatIfSimulatorPanel.test.tsx asserts the literal figure against a fixture
 * where baseline and scenario genuinely differ.
 */

const decimal = (value: number, places = 1): string => String(Number(value.toFixed(places)));

const formatDelta = (delta: number): string => {
  if (delta === 0) return "no change";
  const rounded = decimal(Math.abs(delta));
  return delta > 0 ? `+${rounded}` : `-${rounded}`;
};

function markerPercent(value: number, min: number, max: number): number {
  if (max <= min) return 50;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

export function ForecastDeltaRibbon({
  baseline,
  scenario,
}: {
  baseline: Metric<ThroughputForecastValue>;
  scenario: Metric<ThroughputForecastValue>;
}): React.ReactNode {
  if (baseline.kind !== "value") {
    return <HonestState kind="nodata" detail={`Forecast: ${baseline.reason}`} />;
  }
  if (scenario.kind !== "value") {
    // AC6 — the engine itself refuses lever math against an already-N/A baseline (scenario-engine.ts:
    // "no lever's apply() is invoked below this line"), so a scenario response can only be N/A here
    // when the baseline was too — this mirrors that honesty rather than papering over it.
    return <HonestState kind="nodata" detail={`Scenario: ${scenario.reason}`} />;
  }

  const b = baseline.value;
  const s = scenario.value;
  const deltaP50 = s.p50 - b.p50;
  const deltaP85 = s.p85Adjusted - b.p85Adjusted;
  const min = Math.min(b.p50, b.p85Adjusted, s.p50, s.p85Adjusted);
  const max = Math.max(b.p50, b.p85Adjusted, s.p50, s.p85Adjusted);
  // design/07-design-system.md §8 names a --t-ribbon 280ms motion token for this exact animation;
  // this repository has no design/ directory to source the token from (verified — see this story's
  // report), so the duration is applied directly, inline, per marker.
  const RIBBON_TRANSITION_MS = 280;

  return (
    <div className="ribbon" data-testid="forecast-delta-ribbon">
      <div className="date">
        <span className="num">{decimal(s.p50)}</span>
        <small>periods · p50</small>
        <span
          className="gain"
          data-testid="delta-p50"
          aria-label={`p50 change: ${deltaP50 >= 0 ? "+" : ""}${decimal(deltaP50)} periods`}
        >
          {formatDelta(deltaP50)}
        </span>
      </div>
      <div className="meta">
        p85: {decimal(s.p85Adjusted)} periods (
        <span data-testid="delta-p85">{formatDelta(deltaP85)}</span>)
      </div>
      <div className="chart" aria-hidden="true">
        <div
          className="mk p50"
          style={{
            left: `${markerPercent(s.p50, min, max)}%`,
            transitionProperty: "left",
            transitionDuration: `${RIBBON_TRANSITION_MS}ms`,
          }}
        />
        <div
          className="mk"
          style={{
            left: `${markerPercent(s.p85Adjusted, min, max)}%`,
            transitionProperty: "left",
            transitionDuration: `${RIBBON_TRANSITION_MS}ms`,
          }}
        />
      </div>
    </div>
  );
}
